// components/project/EconomicLineDocuments.js
// Modal per gestione documenti righe Quadro Economico - Solo Admin/Administration

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  Upload, Download, Link2, Unlink, FileText, File, Loader2,
  X, Paperclip, Plus, Search, Check, AlertCircle
} from 'lucide-react';
import api from '../../lib/api';

// Tipi documento per righe economiche
const ECONOMIC_DOCUMENT_TYPES = {
  supplier_quote: 'Preventivo fornitore',
  supplier_invoice: 'Fattura fornitore',
  purchase_order: 'Ordine fornitore',
  delivery_note: 'DDT',
  payment_receipt: 'Ricevuta pagamento',
  subcontract: 'Contratto subappalto',
  other_internal: 'Altro documento interno'
};

// Formatta dimensione file
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Componente badge numero documenti
export const DocumentCountBadge = ({ count, onClick }) => {
  if (!count || count === 0) {
    return (
      <Button 
        variant="ghost" 
        size="sm" 
        className="h-7 px-2 text-gray-400 hover:text-gray-600"
        onClick={onClick}
        title="Gestisci documenti"
      >
        <Paperclip className="w-4 h-4" />
        <span className="ml-1 text-xs">0</span>
      </Button>
    );
  }
  
  return (
    <Button 
      variant="ghost" 
      size="sm" 
      className="h-7 px-2 text-blue-600 hover:text-blue-800"
      onClick={onClick}
      title={`${count} documento/i collegato/i`}
    >
      <Paperclip className="w-4 h-4" />
      <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] justify-center bg-blue-100 text-blue-700">
        {count}
      </Badge>
    </Button>
  );
};

// Modal principale gestione documenti
const EconomicLineDocumentsModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  line,
  onDocumentChange  // callback per aggiornare la lista righe
}) => {
  const [documents, setDocuments] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  
  // Stati per upload
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('supplier_invoice');
  const [uploadNotes, setUploadNotes] = useState('');
  
  // Stati per link documento esistente
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [selectedDocToLink, setSelectedDocToLink] = useState(null);

  // Carica documenti
  const fetchDocuments = useCallback(async () => {
    if (!line?.id || !projectId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/economic-lines/${line.id}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (error.response?.status !== 404) {
        toast.error('Errore nel caricamento documenti');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, line?.id]);

  // Carica documenti disponibili per link
  const fetchAvailableDocuments = useCallback(async () => {
    if (!line?.id || !projectId) return;
    
    try {
      const response = await api.get(`/projects/${projectId}/economic-lines/${line.id}/available-documents`);
      setAvailableDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching available documents:', error);
    }
  }, [projectId, line?.id]);

  useEffect(() => {
    if (isOpen && line?.id) {
      fetchDocuments();
      fetchAvailableDocuments();
    }
  }, [isOpen, line?.id, fetchDocuments, fetchAvailableDocuments]);

  // Upload nuovo documento
  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Seleziona un file');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', uploadType);
      if (uploadNotes) formData.append('notes', uploadNotes);

      await api.post(
        `/projects/${projectId}/economic-lines/${line.id}/documents/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Documento caricato');
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadType('supplier_invoice');
      setUploadNotes('');
      fetchDocuments();
      if (onDocumentChange) onDocumentChange();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  // Collega documento esistente
  const handleLink = async () => {
    if (!selectedDocToLink) {
      toast.error('Seleziona un documento');
      return;
    }

    try {
      setLinking(true);
      
      const formData = new FormData();
      formData.append('document_id', selectedDocToLink);

      await api.post(
        `/projects/${projectId}/economic-lines/${line.id}/documents/link`,
        formData
      );

      toast.success('Documento collegato');
      setShowLinkForm(false);
      setSelectedDocToLink(null);
      setLinkSearchQuery('');
      fetchDocuments();
      fetchAvailableDocuments();
      if (onDocumentChange) onDocumentChange();
    } catch (error) {
      console.error('Link error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il collegamento');
    } finally {
      setLinking(false);
    }
  };

  // Scollega documento
  const handleUnlink = async (documentId) => {
    try {
      await api.delete(
        `/projects/${projectId}/economic-lines/${line.id}/documents/${documentId}/unlink`
      );

      toast.success('Documento scollegato');
      fetchDocuments();
      fetchAvailableDocuments();
      if (onDocumentChange) onDocumentChange();
    } catch (error) {
      console.error('Unlink error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante lo scollegamento');
    }
  };

  // Scarica documento
  const handleDownload = async (doc) => {
    try {
      const response = await api.get(
        `/projects/${projectId}/economic-lines/${line.id}/documents/${doc.id}/download`,
        { responseType: 'blob' }
      );

      // Crea link per download
      const blob = new Blob([response.data], { type: doc.mime_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || 'documento';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Errore durante il download');
    }
  };

  // Filtra documenti disponibili
  const filteredAvailableDocs = availableDocuments.filter(doc => {
    if (!linkSearchQuery) return true;
    const query = linkSearchQuery.toLowerCase();
    return (
      doc.original_filename?.toLowerCase().includes(query) ||
      doc.document_type?.toLowerCase().includes(query) ||
      doc.notes?.toLowerCase().includes(query)
    );
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#FF7819]" />
            Documenti riga economica
          </DialogTitle>
          <DialogDescription>
            {line?.description || 'Gestione documenti'} - {line?.supplier_name || ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Azioni principali */}
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={() => { setShowUploadForm(true); setShowLinkForm(false); }}
              className="bg-[#FF7819] hover:bg-[#e56a10]"
            >
              <Upload className="w-4 h-4 mr-1" />
              Carica documento
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => { setShowLinkForm(true); setShowUploadForm(false); }}
            >
              <Link2 className="w-4 h-4 mr-1" />
              Collega esistente
            </Button>
          </div>

          {/* Form Upload */}
          {showUploadForm && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Carica nuovo documento</h4>
                <Button variant="ghost" size="sm" onClick={() => setShowUploadForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label>File *</Label>
                  <Input 
                    type="file" 
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG - Max 50MB</p>
                </div>
                
                <div>
                  <Label>Tipo documento *</Label>
                  <Select value={uploadType} onValueChange={setUploadType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ECONOMIC_DOCUMENT_TYPES).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Note (opzionale)</Label>
                  <Textarea 
                    value={uploadNotes}
                    onChange={(e) => setUploadNotes(e.target.value)}
                    placeholder="Note aggiuntive..."
                    rows={2}
                    className="mt-1"
                  />
                </div>
                
                <Button 
                  onClick={handleUpload} 
                  disabled={!uploadFile || uploading}
                  className="w-full bg-[#FF7819] hover:bg-[#e56a10]"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Caricamento...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Carica
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Form Link documento esistente */}
          {showLinkForm && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Collega documento esistente</h4>
                <Button variant="ghost" size="sm" onClick={() => setShowLinkForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Cerca documento..."
                  value={linkSearchQuery}
                  onChange={(e) => setLinkSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <div className="max-h-48 overflow-y-auto border rounded bg-white">
                {filteredAvailableDocs.length === 0 ? (
                  <p className="text-center text-gray-500 py-4 text-sm">
                    Nessun documento disponibile
                  </p>
                ) : (
                  filteredAvailableDocs.map(doc => (
                    <div 
                      key={doc.id}
                      className={`
                        flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0
                        ${selectedDocToLink === doc.id ? 'bg-blue-50' : ''}
                      `}
                      onClick={() => setSelectedDocToLink(doc.id)}
                    >
                      <div className={`
                        w-5 h-5 rounded border flex items-center justify-center
                        ${selectedDocToLink === doc.id ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}
                      `}>
                        {selectedDocToLink === doc.id && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                        <p className="text-xs text-gray-500">
                          {doc.document_type_label || doc.document_type} • {formatFileSize(doc.size_bytes)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <Button 
                onClick={handleLink} 
                disabled={!selectedDocToLink || linking}
                className="w-full"
                variant="outline"
              >
                {linking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Collegamento...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Collega selezionato
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Lista documenti collegati */}
          <div className="border rounded-lg">
            <div className="bg-gray-100 px-4 py-2 border-b">
              <h4 className="font-medium text-sm">
                Documenti collegati ({documents.length})
              </h4>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Paperclip className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Nessun documento collegato</p>
              </div>
            ) : (
              <div className="divide-y">
                {documents.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                    <File className="w-8 h-8 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Badge variant="secondary" className="text-xs">
                          {ECONOMIC_DOCUMENT_TYPES[doc.document_type] || doc.document_type_label || doc.document_type}
                        </Badge>
                        <span>{formatFileSize(doc.size_bytes)}</span>
                        {doc.uploaded_by_name && (
                          <span>• {doc.uploaded_by_name}</span>
                        )}
                      </div>
                      {doc.notes && (
                        <p className="text-xs text-gray-400 mt-1 truncate">{doc.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        title="Scarica"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-orange-600 hover:text-orange-700"
                        onClick={() => handleUnlink(doc.id)}
                        title="Scollega"
                      >
                        <Unlink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 text-xs text-gray-500 bg-blue-50 p-3 rounded">
            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p>
              Scollegare un documento non lo elimina dallo storage. Il file rimane disponibile 
              per essere collegato ad altre righe o per la gestione documenti del progetto.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EconomicLineDocumentsModal;
